/**
 * Builds the request_context object sent with Vision AI createMessage requests.
 */

import { useFilterSidePanel } from '@dashhudson/dashing-ui/filter-side-panel';
import { computed, isRef } from 'vue';
import { useRoute } from 'vue-router';

import { COMMUNITY_ROUTE_NAMES } from '@/app/community/constants';
import { useIdentityStore } from '@/stores/identity';

export interface RequestContextRoute {
  name?: string | symbol | null;
}

export interface RequestContext {
  route?: RequestContextRoute;
  filters?: Record<string, unknown>;
  buttonIdentifier?: string;
  currentBrandId: number;
  /** Optional extra context (e.g. messageId for thread summarization). */
  extra?: Record<string, unknown>;
}

/**
 * @param route - Current route (from useRoute())
 * @param filters - Applied filters for the current view (e.g. intentQuery or subset)
 * @param buttonIdentifier - One of REQUEST_CONTEXT_BUTTON_IDENTIFIERS
 * @param currentBrandId - Current brand id
 * @param extra - Optional extra context (e.g. { messageId } for thread scope)
 */
export function buildRequestContext(
  route: RequestContextRoute | null | undefined,
  currentBrandId: number,
  filters?: Record<string, unknown> | null | undefined,
  buttonIdentifier?: string,
  extra?: Record<string, unknown>,
): RequestContext {
  return {
    route: route
      ? {
          name: route.name,
        }
      : undefined,
    filters: filters ?? {},
    buttonIdentifier,
    currentBrandId,
    ...(extra && Object.keys(extra).length > 0 && { extra }),
  };
}

function withValues(obj: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => {
      if (v === null || v === undefined) return false;
      if (Array.isArray(v) && v.length === 0) return false;
      if (v === '') return false;
      return true;
    }),
  );
}

interface SourceUpdatedFilter {
  range?: { start?: string; end?: string };
}

function flattenCommunityFilters(
  raw: Record<string, unknown>,
  routeQuery: Record<string, string | string[]>,
): Record<string, unknown> {
  const sourceUpdated = raw.sourceUpdated as SourceUpdatedFilter | undefined;
  return {
    messageTags: raw.tagIds,
    tagOperator: raw.tagOperator,
    sourceUpdatedAfter: sourceUpdated?.range?.start,
    sourceUpdatedBefore: sourceUpdated?.range?.end,
    platforms: raw.platforms,
    messageTypes: raw.messageTypes,
    messageState: raw.messageState,
    messageContent: raw.messageContent,
    sentiments: raw.sentiments,
    assigneeIds: raw.assigneeIds,
    brandIds: raw.brandIds,
    brandTagIds: raw.brandTagIds,
    interactionRuleIds: raw.interactionRuleIds,
    query: (routeQuery?.query as string) || undefined,
  };
}

/**
 * Composable that returns a reactive `filters` object for the current page.
 *
 * For community routes the raw side-panel filters are flattened into a defined
 * structure so that Vision AI receives consistent, named fields regardless of
 * which component triggers a message.
 */
export function usePageFilters() {
  const route = useRoute();
  const identityStore = useIdentityStore();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const filterSidePanel = useFilterSidePanel() as any;

  return computed<Record<string, unknown>>(() => {
    const f = filterSidePanel?.scopedFilters ?? filterSidePanel?.filters;
    if (!f) return {};
    const raw = isRef(f) ? f.value : f;
    const rawFilters =
      typeof raw === 'object' && raw !== null && !Array.isArray(raw)
        ? ({ ...raw } as Record<string, unknown>)
        : {};

    const routeName = route?.name?.toString() ?? '';
    if (routeName.startsWith('community.')) {
      const flattened = flattenCommunityFilters(
        rawFilters,
        route.query as Record<string, string | string[]>,
      );
      if (routeName === COMMUNITY_ROUTE_NAMES.ASSIGNED_TO_YOU && identityStore.identity?.id) {
        flattened.assigneeIds = [identityStore.identity.id];
      }
      return withValues(flattened);
    }

    return withValues(rawFilters);
  });
}
